import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { addLocale, FilterMatchMode, locale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableExpandedRows,
  DataTableFilterMetaData,
  DataTableRowEditCompleteEvent,
  DataTableSortMeta,
  DataTableStateEvent,
  DataTableValueArray
} from 'primereact/datatable';
import { useDebounce as usePrimereactDebounce } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';

import { ActualJobTemplate } from '../../constants';
import { useDebounce } from '../../hooks';
import useOperationTypesService from '../../hooks/services/useOperationTypesService';
import { ruLocale } from '../../lang/ruLocale';
import { ActualOperation, DropdownObjectType, OperationAttainment } from '../../types/interfaces';
import { ObjectForSelection } from '../../types/interfaces/ObjectForSelection';
import { ActualJobsProps } from '../../types/props';
import { clearFilter, generateGUID } from '../../utils';

import {
  ActualOperationsEditCtx,
  AttainmentTagsFromContext,
  ExecutorEditCellWithCtx,
  MeasureEditorCell,
  PlannedEffortEditorCell
} from './ActualOperationsEditCells';
import { createDropdownEditor, textEditor } from './Editors';
import NestedActualJobs from './NestedActualOperations';
import ActualJobBodyTemplate, { JobIdTemplate } from './Templates';
import UnitMeasurementTemplate from './UnitMeasurementTemplate';

const COLUMN_WIDTHS = {
  changed: '17px',
  id: '90px',
  operation: '150px',
  attainment: '120px',
  volume: '90px',
  measure: '100px',
  employees: '180px',
  ktu: '56px',
  comment: '120px',
  edit: '56px',
  delete: '56px'
} as const;

const ActualOperations: React.FC<ActualJobsProps> = ({
  tagScrollHeight,
  employees,
  rawEmployeesData,
  actualOperations = [],
  setActualOperations,
  plannedOperations,
  plannedOperationsAttainments,
  plannedOperationsOptions,
  allOperationsAttainments,
  department,
  workOrderTimeframe,
}) => {
  const [scrollHeight, setScrollHeight] = useState('30wh');
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<string | number>('');
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);
  const [selectedJob, setSelectedJob] =
    useState<ActualOperation>(ActualJobTemplate);
  const dt = useRef<DataTable<ActualOperation[]>>(null);
  const toast = useRef<Toast>(null);
  const dataTableRef = useRef<HTMLDivElement>(null); // This is the ref for the container that includes the DataTable
  const idRef = useRef<HTMLInputElement>(null);
  const actualJobRef = useRef<HTMLInputElement>(null);
  const unitMeasurementRef = useRef<HTMLInputElement>(null);
  const attaimentRef = useRef<HTMLInputElement>(null);
  const jobVolumeRef = useRef<HTMLInputElement>(null);
  const commentRef = useRef<HTMLInputElement>(null);
  const plannedEffortRef = useRef<HTMLInputElement>(null);
  const [editingRows, setEditingRows] = useState<number[]>([]);
  const [rowIdInEdit, setRowIdInEdit] = useState<string>('');
  const [operationByRowId, setOperationByRowId] = useState<Record<string, string | number>>({});
  const [editorUpdateCounter, setEditorUpdateCounter] = useState<Record<string, number>>({});
  const [filters, debouncedFilters, setFilters] = usePrimereactDebounce<
    Record<string, DataTableFilterMetaData>
  >({
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    'operation.label': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS
    },
    'measure.label': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS
    },
    '_attainmentText': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS
    },
    plannedEffort: { value: null, matchMode: FilterMatchMode.CONTAINS },
    volume: { value: null, matchMode: FilterMatchMode.CONTAINS },
    comment: { value: null, matchMode: FilterMatchMode.CONTAINS }
  }, 500);
  const [actualOperationsAttainments, setActualOperationsAttainments] = useState<OperationAttainment[]>([]);
  const operationTypesService = useOperationTypesService();

  const actualJobKeys = new Set(
    Array.isArray(actualOperations)
      ? actualOperations.map((job) => job.operation.key)
      : []
  );

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setMultiSortMeta(e.multiSortMeta);
  }, []);
  const isFilterable = editingRows.length > 0 ? false : true;

  const isSortingOrFiltering = useMemo(() => {
    const isSorted = !!multiSortMeta?.length ? true : false;
    const anyFilterHasValue = Object.values(filters).some(
      (filter: any) => filter.value?.length
    );
    const isSortingOrFiltering = isSorted || anyFilterHasValue;
    return isSortingOrFiltering;
  }, [multiSortMeta, filters]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    
    if (_filters && _filters[filter]) {
      _filters[filter].value = value;
      setFilters(_filters);
    } else {
      console.warn(`Filter '${filter}' not found in filters object`);
    }
  };

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const getTagStyle = (tagKey: string | number) => {
    const isPlanned = plannedOperations?.some((item) => item.key === tagKey);
    const isActual = actualJobKeys.has(tagKey);
    if (!isPlanned && isActual) {
      return { color: '#616161', fontWeight: '500' };
    } else {
      return { color: 'rgb(37 107 63)', fontWeight: '500' };
    }
  };

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const convertToDropdownObjectType = (
    object: ObjectForSelection
  ): DropdownObjectType => {
    return {
      key: object.id,
      label: object.label
    };
  };


  const getOperationById = useCallback(
    (operationId: string, rowId: string) => {
      const serviceResult = operationTypesService.get(Number(operationId));
      serviceResult.result.then((operation) => {
        if (operation) {
          setActualOperations((prevOperations) =>
            prevOperations.map((job) => {
              if (job.id === rowId) {
                const updatedMeasure = operation.measure
                  ? convertToDropdownObjectType(operation.measure)
                  : job.measure;
                const updatedJob: ActualOperation = {
                  ...job,
                  measure: updatedMeasure
                };
                return updatedJob;
              }
              return job;
            })
          );
        }
      });
      return () => serviceResult.cancel();
    },
    [operationTypesService, setActualOperations]
  );

  const [measures, setMeasures] = useState<{
    [key: string]: DropdownObjectType;
  }>({});
  
  const [efforts, setEfforts] = useState<{
    [key: string]: number;
  }>({});

  const FetchedMeasure = async (id: number): Promise<DropdownObjectType> => {
    const result = await operationTypesService.get(id).result;
    if (result?.measure) {
      return {
        key: result.measure.id.toString(),
        label: result.measure.label
      };
    }
    return { key: '', label: '' };
  };

  const editingOperationCallback = useCallback(
    async (v: { id: number }) => {
      const result = await operationTypesService.get(v.id).result;
      
      if (result) {
        if (result.measure) {
          const measure = {
            key: result.measure.id.toString(),
            label: result.measure.label
          };
          setMeasures((prev) => ({
            ...prev,
            [v.id.toString()]: measure
          }));
        }
        
        if (result.effort !== undefined) {
          setEfforts((prev) => ({
            ...prev,
            [v.id.toString()]: Number(result.effort) || 0
          }));
        }
      }
    },
    [department, operationTypesService]
  );

  const getAttainmentsForOperation = useCallback((operation: DropdownObjectType) => {
    if (!operation?.key || !actualOperationsAttainments?.length) return [];
    
    const attainment = actualOperationsAttainments.find(
      item => item.operationType.id.toString() === operation.key.toString()
    );
    return attainment?.attainments || [];
  }, [actualOperationsAttainments]);

  const getAttainmentsForOperations = useCallback((operationIds: number[]) => {
    if (!operationIds.length) {
      setActualOperationsAttainments([]);
      return;
    }

    if (allOperationsAttainments && allOperationsAttainments.length > 0) {
      const filteredAttainments = allOperationsAttainments.filter(att =>
        operationIds.includes(att.operationType.id)
      );
      setActualOperationsAttainments(filteredAttainments);
      return;
    }

    setActualOperationsAttainments([]);
  }, [allOperationsAttainments]);

  // Эффект для первоначальной загрузки навыков
  useEffect(() => {
    const operationIds = actualOperations
      .map(op => Number(op.operation.key))
      .filter(id => !isNaN(id) && id !== 0);
  
    getAttainmentsForOperations(operationIds);
  }, [actualOperations, getAttainmentsForOperations]);

  // Функция для получения навыков для одной операции (при изменении в редакторе)
  const getAttainmentForSingleOperation = useCallback((operationKey: string | number, rowId?: string) => {
    const operationId = Number(operationKey);
    if (isNaN(operationId) || operationId === 0) return;

    if (allOperationsAttainments && allOperationsAttainments.length > 0) {
      const foundAttainment = allOperationsAttainments.find(
        att => att.operationType.id === operationId
      );
      
      if (foundAttainment) {
        setActualOperationsAttainments(prev => {
          const filtered = prev.filter(item => 
            item.operationType.id !== foundAttainment.operationType.id
          );
          return [...filtered, foundAttainment];
        });
      }
    }
  }, [allOperationsAttainments]);
  

  const attainmentBodyTemplate = useCallback((job: ActualOperation) => {
    if (!job?.operation) return null;

    return <AttainmentTagsFromContext operation={job.operation} />;
  }, []);

  const enhancedOperations = useMemo(() => {
    return actualOperations.map(op => ({
      ...op,
      _attainmentText: getAttainmentsForOperation(op.operation)
        .map(a => a.label)
        .join(', ')
    }));
  }, [actualOperations, getAttainmentsForOperation]);

  const [originalOperations, setOriginalOperations] = useState<
    ActualOperation[]
  >([]);
  const originalInitialized = useRef(false);

  useEffect(() => {
    if (
      !originalInitialized.current &&
      Array.isArray(actualOperations) &&
      actualOperations.length > 0
    ) {
      setOriginalOperations(JSON.parse(JSON.stringify(actualOperations)));
      originalInitialized.current = true;
    }
  }, [actualOperations]);

  const sanitizeForCompare = (op: ActualOperation) => {
    if (!op) return null;

    const employeesIds = (op.employees || [])
      .map((e: any) => {
        return e?.id ?? e?.key ?? e;
      })
      .filter(Boolean)
      .map(String)
      .sort();

    return {
      id: op.id,
      operation: {
        key: op.operation?.key?.toString() ?? '',
        label: op.operation?.label ?? ''
      },
      measure: {
        key: op.measure?.key?.toString?.() ?? op.measure?.key ?? '',
        label: op.measure?.label ?? ''
      },
      volume: op.volume ?? '',
      employees: employeesIds,
      comment: op.comment ?? ''
    };
  };

  const normalizeMeasure = (m: any): DropdownObjectType => ({
    key: m?.key?.toString?.() ?? m?.id?.toString?.() ?? '',
    label: m?.label ?? ''
  });

  const onRowEditComplete = useCallback(
    async (e: DataTableRowEditCompleteEvent) => {
      const _products = [...actualOperations];
      const { newData, index } = e;

      const measure = await FetchedMeasure(newData.operation.key);
      if (measure) {
        newData.measure = normalizeMeasure(measure);
      } else if (newData.measure) {
        newData.measure = normalizeMeasure(newData.measure);
      }

      const oldRow = _products[index];
      const originalRow = originalOperations.find((r) => r.id === newData.id);

      const sanitizedOriginal = sanitizeForCompare(originalRow ?? oldRow);
      const sanitizedNew = sanitizeForCompare({ ...oldRow, ...newData });

      const isChanged = (() => {
        if (newData.generatedID) return true;
        if (!sanitizedOriginal) return true;
        return (
          JSON.stringify(sanitizedOriginal) !== JSON.stringify(sanitizedNew)
        );
      })();

      setEditingRows((prev) => prev.filter((item) => item !== newData.id));
      const updatedRow: ActualOperation = {
        ...oldRow,
        ...newData,
        changed: isChanged ? true : false
      };

      _products[index] = updatedRow;
      setActualOperations(_products);

      const selectedOperationId = newData.operation.key;
      if (selectedOperationId && isChanged) {
        getOperationById(selectedOperationId, newData.id);
      }
    },
    [editingRows, getOperationById, actualOperations, originalOperations]
  );

  const handleDeleteConfirmation = () => {
    handleDelete(itemToDelete);
    setDeleteDialogVisible(false);
    setEditingRows((prev) =>
      prev.filter((item) => item.toString() !== itemToDelete.toString())
    );
    setItemToDelete('');
  };
  const handleDelete = (id: string | number) => {
    if (id === rowIdInEdit) {
      setRowIdInEdit('');
      setEditingRows(editingRows.filter((rowIndex) => rowIndex !== Number(id)));
    }
    const updatedJobs = actualOperations.filter((job) => job.id !== id);
    setActualOperations(updatedJobs);
  };

  // TODO вынести остальные темплейты в темплейты, со след задачей
  const executorBodyTemplate = (job: ActualOperation | undefined) => {
    if (!job || !job.employees) {
      console.error('Invalid job data:', job);
      return null;
    }
    const isExpanded =
      Array.isArray(expandedRows) &&
      expandedRows.some((row) => row.id === job.id);

    return (
      <div className="flex align-items-center">
        <div className="mr-2">Исполнители ({job.employees.length})</div>
        <Button
          icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
          outlined
          rounded
          onClick={() => {
            if (Array.isArray(expandedRows)) {
              if (isExpanded) {
                setExpandedRows(
                  expandedRows.filter((row) => row.id !== job.id)
                );
              } else {
                setExpandedRows([...expandedRows, job]);
              }
            } else {
              setExpandedRows([job]);
            }
          }}
        />
      </div>
    );
  };

  const plannedEffortBodyTemplate = (job: ActualOperation) => {
    const effortFromState = job.operation?.key ? efforts[job.operation.key] : undefined;
    const effortFromOperation = job.operation?.effort;
    const effort = effortFromState ?? effortFromOperation;
    return <span>{effort}</span>;
  };

  const rowExpansionTemplate = (data: ActualOperation) => {
    return <NestedActualJobs operation={data} />;
  };

  const deleteButton = (operation: ActualOperation | undefined) => {
    const color = isSortingOrFiltering ? '#ef444475' : '';

    if (!operation) {
      console.error('Invalid operation data:', operation);
      return null; // or handle the error accordingly
    }
    return (
      <Button
        icon="pi pi-trash"
        severity="danger"
        style={{ background: color, border: color }}
        disabled={isSortingOrFiltering}
        rounded
        onClick={() => {
          setItemToDelete(operation.id);
          setDeleteDialogVisible(true);
        }}
      />
    );
  };

  const changedRow = (job: ActualOperation | undefined) => {
    if (!job) {
      console.error('Invalid job data:', job);
      return null; // or handle the error accordingly
    }
    return (
      <Button
        icon={`pi ${job.changed && 'pi-exclamation-circle'}`}
        style={{width: 17}}
        rounded
        text
        tooltip="Строка была добавлена или изменена. Сохраните наряд для обновления данных."
      />
    );
  };

  const handleAddNewRow = () => {
    const newRow = {
      id: generateGUID(),
      operation: {
        key: '',
        label: ''
      },
      volume: '',
      measure: {
        key: '',
        label: ''
      },
      employees: [],
      comment: '',
      generatedID: true,
      changed: true // новая строка — помечаем как изменённую
    };
    setActualOperations([newRow, ...actualOperations]);
    // TODO костыль для открытия строки в режиме редактирования
    setTimeout(() => {
      const editButton = document.querySelector(
        '.p-selectable-row .p-row-editor-init'
      );
      const mouseClickEvents = ['mousedown', 'click', 'mouseup'];
      function simulateMouseClick(element: Element) {
        mouseClickEvents.forEach((mouseEventType) =>
          element.dispatchEvent(
            new MouseEvent(mouseEventType, {
              view: window,
              bubbles: true,
              cancelable: true,
              buttons: 1
            })
          )
        );
      }
      editButton && simulateMouseClick(editButton);
      setRowIdInEdit(newRow.id);
    }, 100);
  };

  const footer = () => {
    const color = rowIdInEdit || isSortingOrFiltering ? '#22c55e52' : '';
    return (
      <div className="flex flex-row justify-content-end w-full" style={{padding: '0 15px'}}>
        <div className="flex flex-row justify-content-center" style={{ width: '5.4%' }}>
          <Button
            icon="pi pi-plus"
            aria-label="Добавить работу"
            disabled={rowIdInEdit.length > 0 || isSortingOrFiltering}
            style={{ background: color, border: color }}
            severity="success"
            rounded
            onClick={handleAddNewRow}
            className="p-button actual-ops__add-btn"
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    const calculateHeight = () => {
      if (dataTableRef.current) {
        const tableElement = dataTableRef.current;
        if (tableElement) {
          const tableOffset =
            tableElement.getBoundingClientRect().top + window.pageYOffset;
          const calculatedHeight = `calc(100vh - ${
            tableOffset + 60 + (actualOperations.length > 0 ? 60 : 0)
          }px)`;
          setScrollHeight(calculatedHeight);
        }
      }
    };

    // Initial calculation and setup resize observer
    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, [actualOperations, tagScrollHeight]);

  useEffect(() => {
    if (actualOperations.length > 0) {
      const initialEfforts: { [key: string]: number } = {};
      
      actualOperations.forEach(job => {
        if (job.operation?.key && job.operation?.effort !== undefined) {
          initialEfforts[job.operation.key.toString()] = Number(job.operation.effort);
        }
      });
      
      setEfforts(initialEfforts);
    }
  }, [actualOperations]);

  const rowEditValidator = (rowData: ActualOperation) => {
    // Check if the required fields are filled in
    if (!rowData.operation.key || !rowData.employees?.length) {
      // If any required field is empty, return false to prevent saving
      if (toast.current)
        toast.current.show({
          severity: 'warn',
          summary: 'Ошибка',
          detail: 'Пожалуйста заполните обязательные поля.'
        });
      return false;
    }

    if (rowIdInEdit === rowData.id) setRowIdInEdit('');
    setEditingRows(editingRows.filter((rowIndex) => rowIndex !== rowData.id));

    return true;
  };

  const actualOperationsEditCtxValue = useMemo(
    () => ({
      measures,
      efforts,
      getAttainmentsForOperation,
      actualOperations,
      actualOperationsAttainments,
      department,
      employees,
      rawEmployeesData,
      plannedOperationsAttainments,
      allOperationsAttainments,
      operationByRowId,
      editorUpdateCounter,
      workOrderTimeframe
    }),
    [
      measures,
      efforts,
      getAttainmentsForOperation,
      actualOperations,
      actualOperationsAttainments,
      department,
      employees,
      rawEmployeesData,
      plannedOperationsAttainments,
      allOperationsAttainments,
      operationByRowId,
      editorUpdateCounter,
      workOrderTimeframe
    ]
  );

  return (
    <div
      ref={dataTableRef}
      style={{
        width: '100%',
        overflow: 'auto',
        border: '1px solid rgb(211, 211, 211)',
        borderRadius: '5px',
        minHeight: '42px',
        alignContent: 'center',
        padding: 0
      }}
      className="actualOperations-table wrapper wrapper--main"
    >
      <Toast ref={toast} />
      <Tooltip target=".actual-op-deleted-icon" />

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="фактическую работу"
        entityName={selectedJob?.operation.label}
      />
      <ActualOperationsEditCtx.Provider value={actualOperationsEditCtxValue}>
        <DataTable
          onRowEditCancel={(event) => {
            setEditingRows(
              editingRows.filter((rowIndex) => rowIndex !== event.data.id)
            );
            setRowIdInEdit('');
          }}
          expandedRows={expandedRows}
          rowExpansionTemplate={rowExpansionTemplate}
          onRowToggle={(e) => setExpandedRows(e.data)}
          onRowEditInit={(event) => {
            setEditingRows((prev) => [...prev, event.data.id]);
            setRowIdInEdit(event.data.id);
            // Инициализируем операцию для этой строки
            if (event.data.operation?.key) {
              setOperationByRowId(prev => ({
                ...prev,
                [event.data.id]: event.data.operation.key
              }));
            }
          }}
          footer={footer}
          rowEditValidator={rowEditValidator}
          ref={dt}
          scrollHeight={scrollHeight}
          selectionMode="single"
          selection={selectedJob!}
          onSelectionChange={(e) => setSelectedJob(e.value)}
          onRowEditComplete={onRowEditComplete}
          metaKeySelection={false}
          scrollable
          sortMode="multiple"
          removableSort
          size="small"
          value={enhancedOperations}
          dataKey="id"
          filterDisplay="row"
          emptyMessage="Фактические работы не найдены."
          onRowReorder={(e) => setActualOperations(e.value)}
          editMode="row"
          filters={debouncedFilters}
          multiSortMeta={multiSortMeta}
          onSort={setMultiSortMetaCallback}
          pt={{
            wrapper: {
              style: { 
                scrollbarGutter: 'stable both-edges',
                tableLayout: 'fixed'
              }
            },
            table: {
              style: {
                width: '100%',
                minWidth: '1200px'
              }
            }
          }}
        >
          <Column
            body={changedRow}
            columnKey="changed"
            field="changed"
            bodyStyle={{ textAlign: 'center' }}
            style={{ 
              width: COLUMN_WIDTHS.changed,
              minWidth: COLUMN_WIDTHS.changed,
              maxWidth: COLUMN_WIDTHS.changed
            }}
          />
          <Column
            columnKey="id"
            field="id"
            header="ID работы"
            body={JobIdTemplate}
            style={{ 
              width: COLUMN_WIDTHS.id,
              minWidth: COLUMN_WIDTHS.id,
              maxWidth: COLUMN_WIDTHS.id
            }}
            filterElement={
              <div className="flex justify-content-end">
                <InputText ref={idRef} onChange={handleInputChange('id')} />
              </div>
            }
            onFilterClear={() => clearFilter('id', filters, setFilters, idRef)}
            filter={isFilterable}
            sortable={isFilterable}
          />
          <Column
            field="operation"
            filterField="operation.label"
            sortField="operation.label"
            header="Фактические работы"
            style={{ 
              width: COLUMN_WIDTHS.operation,
              minWidth: COLUMN_WIDTHS.operation,
              maxWidth: COLUMN_WIDTHS.operation
            }}
            body={(job) => {
              const isOperationDeleted = job.operation?.key &&
              String(job.operation.key).length > 0 &&
              !plannedOperationsOptions.some(opt =>
                String(opt.key) === String(job.operation.key)
              );
              return (
                <div className="flex align-items-center gap-1">
                  <ActualJobBodyTemplate job={job} getTagStyle={getTagStyle} />
                  {isOperationDeleted && (
                    <i
                      className="pi pi-exclamation-circle actual-op-deleted-icon flex-shrink-0"
                      style={{ color: 'rgb(255, 168, 0)', fontSize: '0.9rem' }}
                      data-pr-tooltip="Удалено в справочнике"
                    />
                  )}
                </div>
              );
            }}
            editor={(options) => {
              const originalCallback = options.editorCallback;
              const rowId = options.rowData.id;
              options.editorCallback = (v) => {
                if (v?.id) {
                  setTimeout(() => {
                    setOperationByRowId(prev => ({
                      ...prev,
                      [rowId]: v.id
                    }));
                    setEditorUpdateCounter(prev => ({
                      ...prev,
                      [rowId]: (prev[rowId] || 0) + 1
                    }));
                    getAttainmentForSingleOperation(v.id, rowId);
                  }, 0);
                }
                editingOperationCallback(v);
              originalCallback!(v);
              };
              return createDropdownEditor(
                options,
                plannedOperationsOptions,
                'Выберите работу',
                true
              );
            }}
            onCellEditComplete={(e) => {
              if (e.field === 'operation' && e.newValue?.key) {
                const operationTypeId = Number(e.newValue.key);
                const rowId = (e).rowData?.id || (e as any).data?.id;
                if (rowId) {
                  setOperationByRowId(prev => ({
                    ...prev,
                    [rowId]: operationTypeId
                  }));
                  setEditorUpdateCounter(prev => ({
                    ...prev,
                    [rowId]: (prev[rowId] || 0) + 1
                  }));
                  getAttainmentForSingleOperation(operationTypeId, rowId);
                }
              }
            }}
            filterElement={
              <div className="flex justify-content-end">
                <InputText
                  ref={actualJobRef}
                  onChange={handleInputChange('operation.label')}
                />
              </div>
            }
            onFilterClear={() =>
              clearFilter('operation.label', filters, setFilters, actualJobRef)
            }
            filter={isFilterable}
            sortable={isFilterable}
          />
          <Column
            field="_attainmentText"
            filterField="_attainmentText"
            sortField="_attainmentText"
            header="Требуемые навыки"
            body={attainmentBodyTemplate}
            editor={(options) => (
              <AttainmentTagsFromContext
                operation={options.rowData.operation}
                rowId={options.rowData.id}
              />
            )}
            style={{ 
              width: COLUMN_WIDTHS.attainment,
              minWidth: COLUMN_WIDTHS.attainment,
              maxWidth: COLUMN_WIDTHS.attainment
            }}
            filter={isFilterable}
            onFilterClear={() =>
              clearFilter(
                '_attainmentText',
                filters,
                setFilters,
                attaimentRef
              )
            }
            filterElement={
              <div className="flex justify-content-end">
                <InputText
                  ref={attaimentRef}
                  onChange={handleInputChange('_attainmentText')}
                />
              </div>
            }
          />
          <Column
            columnKey="volume"
            field="volume"
            header="Объем работ"
            editor={(options) => textEditor(options, 'number', 13)}
            filter={isFilterable}
            style={{ 
              width: COLUMN_WIDTHS.volume,
              minWidth: COLUMN_WIDTHS.volume,
              maxWidth: COLUMN_WIDTHS.volume
            }}
            onFilterClear={() =>
              clearFilter('volume', filters, setFilters, jobVolumeRef)
            }
            filterElement={
              <div className="flex justify-content-end">
                <InputText
                  ref={jobVolumeRef}
                  onChange={handleInputChange('volume')}
                />
              </div>
            }
            sortable={isFilterable}
          />
          <Column
            field="measure"
            filterField="measure.label"
            sortField="measure.label"
            header="Единица измерения"
            style={{ 
              width: COLUMN_WIDTHS.measure,
              minWidth: COLUMN_WIDTHS.measure,
              maxWidth: COLUMN_WIDTHS.measure
            }}
            body={UnitMeasurementTemplate}
            editor={(options) => <MeasureEditorCell options={options} />}
            filter={isFilterable}
            onFilterClear={() =>
              clearFilter(
                'measure.label',
                filters,
                setFilters,
                unitMeasurementRef
              )
            }
            filterElement={
              <div className="flex justify-content-end">
                <InputText
                  ref={unitMeasurementRef}
                  onChange={handleInputChange('measure.label')}
                />
              </div>
            }
            sortable={isFilterable}
          />
          <Column
            field="plannedEffort"
            filterField="plannedEffort"
            sortField="plannedEffort"
            header="Трудозатраты (план), мин"
            body={plannedEffortBodyTemplate}
            editor={(options) => <PlannedEffortEditorCell options={options} />}
            style={{ 
              width: COLUMN_WIDTHS.measure,
              minWidth: COLUMN_WIDTHS.measure,
              maxWidth: COLUMN_WIDTHS.measure
            }}
            filter={isFilterable}
            onFilterClear={() =>
              clearFilter('plannedEffort', filters, setFilters, plannedEffortRef) 
            }
            filterElement={
              <div className="flex justify-content-end">
                <InputText
                  ref={plannedEffortRef} 
                  onChange={handleInputChange('plannedEffort')}
                />
              </div>
            }
            sortable={isFilterable}
          />
          <Column
            columnKey="employees"
            field="employees"
            header="Исполнители"
            body={executorBodyTemplate}
            style={{ 
              width: COLUMN_WIDTHS.employees,
              minWidth: COLUMN_WIDTHS.employees,
              maxWidth: COLUMN_WIDTHS.employees
            }}
            editor={(options) => <ExecutorEditCellWithCtx options={options} />}

          />
          <Column 
            header="КТУ" 
            body={1}
            style={{ 
              width: COLUMN_WIDTHS.ktu,
              minWidth: COLUMN_WIDTHS.ktu,
              maxWidth: COLUMN_WIDTHS.ktu
            }}   
          />
          <Column
            columnKey="comment"
            field="comment"
            header="Комментарий"
            style={{ 
              width: COLUMN_WIDTHS.comment,
              minWidth: COLUMN_WIDTHS.comment,
              maxWidth: COLUMN_WIDTHS.comment
            }}
            editor={(options) => textEditor(options, 'text')}
            onFilterClear={() =>
              clearFilter('comment', filters, setFilters, commentRef)
            }
            filter={isFilterable}
            filterElement={
              <div className="flex justify-content-end">
                <InputText
                  ref={commentRef}
                  onChange={handleInputChange('comment')}
                />
              </div>
            }
            showFilterMenu={isFilterable}
            sortable={isFilterable}
          />
          <Column
            rowEditor={!isSortingOrFiltering}
            bodyStyle={{ textAlign: 'center' }}
            style={{ 
              width: COLUMN_WIDTHS.edit,
              minWidth: COLUMN_WIDTHS.edit,
              maxWidth: COLUMN_WIDTHS.edit
            }}
          ></Column>
          <Column
            body={deleteButton}
            style={{ 
              width: COLUMN_WIDTHS.delete,
              minWidth: COLUMN_WIDTHS.delete,
              maxWidth: COLUMN_WIDTHS.delete
            }}

            bodyStyle={{ textAlign: 'center' }}
          ></Column>
        </DataTable>
      </ActualOperationsEditCtx.Provider>
      {editingRows.length > 0 && (
        <Message
          severity="warn"
          text="В таблице есть строки с работами в режиме редактирования"
        />
      )}
      {isSortingOrFiltering && (
        <div className="flex justify-content-end">
          <Message
            severity="warn"
            text="Изменение таблицы запрещено при включенной сортировке или фильтрации"
          />
        </div>
      )}
    </div>
  );
};

export default ActualOperations;